import { selectMediaAsset, copyMediaDefaults, removeMediaUsage } from "./handbook_media_usages"

// Small view for both image placements and hero, sharing the existing form state.
export class HandbookMediaEditor {
  constructor(editor, rows, assets, pickerUrl) {
    if (!Array.isArray(rows)) throw new Error("Niepoprawne użycia mediów")
    this.editor = editor
    this.rows = rows
    this.assets = new Map(assets.map(asset => [asset.id, asset]))
    this.pickerUrl = pickerUrl
    this.dialog = editor.pickerTarget
    this.onClose = () => { this.request?.abort(); this.returnFocus?.focus() }
    this.dialog.addEventListener("close", this.onClose)
  }
  dispose() {
    this.request?.abort()
    this.dialog.removeEventListener("close", this.onClose)
    if (this.dialog.open) this.dialog.close()
  }
  renderHero() {
    const c = this.editor
    c.heroTarget.replaceChildren()
    this.render("hero", c.heroTarget)
    if (this.rows.some(row => row.placement_key === "hero")) c.heroTarget.append(c.button("Usuń hero", () => {
      removeMediaUsage(this.rows, "hero"); this.renderHero(); c.changed()
      c.heroTarget.querySelector("button").focus()
    }))
  }
  render(key, container) {
    const c = this.editor
    const usage = this.rows.find(row => row.placement_key === key)
    const asset = usage && this.assets.get(usage.media_asset_id)
    const view = c.node("div", { className: "space-y-3" })
    container.append(view)
    const refresh = () => {
      if (key === "hero") this.renderHero()
      else view.replaceWith(this.render(key, c.node("div")))
    }
    if (asset) {
      view.append(c.node("p", { textContent: asset.title }))
      view.append(c.node("p", { textContent: asset.archived_at ? "Materiał archiwalny — istniejące użycie można zachować, nowe wymaga aktywnego obrazu." : `Stan materiału: ${asset.processing_status}` }))
      if (asset.thumbnail_url) view.append(c.node("img", { src: asset.thumbnail_url, alt: "", className: "max-w-full h-auto" }))
    } else view.append(c.node("p", { textContent: usage ? `Wybrany materiał jest niedostępny: ${usage.media_asset_id}` : "Nie wybrano obrazu." }))
    const choose = c.button(usage ? "Zmień obraz" : "Wybierz obraz", () => this.open(key, choose, refresh))
    view.append(choose)
    if (!usage) return view
    const warning = c.node("p", { className: "text-sm" })
    const updateWarning = () => { warning.textContent = !usage.decorative && !usage.alt_text.trim() ? "Brak ALT — uzupełnij opis obrazu przed publikacją." : "" }
    const alt = c.field(view, "ALT w tej rewizji", usage.alt_text, value => { usage.alt_text = value; updateWarning() })
    alt.disabled = usage.decorative
    c.field(view, "Podpis w tej rewizji", usage.caption, value => { usage.caption = value })
    const id = `handbook-decorative-${++c.fieldSequence}`
    const checkbox = c.node("input", { id, type: "checkbox", checked: usage.decorative })
    checkbox.addEventListener("change", () => {
      usage.decorative = checkbox.checked
      if (usage.decorative) usage.alt_text = ""
      alt.value = usage.alt_text; alt.disabled = usage.decorative
      updateWarning(); c.changed()
    })
    const label = c.node("label", { htmlFor: id, textContent: "Obraz dekoracyjny" })
    view.append(checkbox, label, warning)
    updateWarning()
    if (asset) view.append(c.button("Pobierz domyślny ALT i podpis", async () => {
      try {
        const response = await fetch(asset.metadata_url, { headers: { Accept: "application/json" }, cache: "no-store" })
        if (!response.ok) throw new Error("metadata")
        const fresh = await response.json()
        if (!c.element.isConnected || !c.hasStatusTarget || !this.rows.includes(usage) || usage.media_asset_id !== asset.id) return
        this.assets.set(fresh.id, fresh); copyMediaDefaults(usage, fresh)
        refresh(); c.changed()
        const host = key === "hero" ? c.heroTarget : c.card(key)
        host.querySelector("textarea:not(:disabled)")?.focus()
        c.statusTarget.textContent = "Pobrano aktualny domyślny ALT i podpis do wersji roboczej."
      } catch {
        if (c.element.isConnected && c.hasStatusTarget) c.statusTarget.textContent = "Nie udało się pobrać metadanych. Zachowano zmiany."
      }
    }))
    return view
  }
  open(key, trigger, refresh) {
    const c = this.editor
    this.returnFocus = trigger
    this.dialog.replaceChildren(c.node("h2", { textContent: "Wybierz obraz", className: "text-xl font-semibold" }))
    const search = c.field(this.dialog, "Szukaj obrazu po tytule", "", () => {}, "input")
    const results = c.node("div", { className: "space-y-3 mt-4" })
    results.setAttribute("aria-live", "polite")
    let page = 1
    const load = async () => {
      this.request?.abort()
      const request = new AbortController()
      this.request = request
      results.replaceChildren(c.node("p", { textContent: "Ładowanie obrazów…" }))
      const url = new URL(this.pickerUrl, window.location.origin)
      url.searchParams.set("q", search.value); url.searchParams.set("page", page)
      try {
        const response = await fetch(url, { signal: request.signal, headers: { Accept: "application/json" }, cache: "no-store" })
        if (!response.ok) throw new Error("picker")
        const data = await response.json()
        if (request.signal.aborted || !this.dialog.open) return
        results.replaceChildren()
        if (!data.assets.length) results.append(c.node("p", { textContent: "Brak gotowych, niearchiwalnych obrazów." }))
        data.assets.forEach(asset => {
          this.assets.set(asset.id, asset)
          const item = c.node("div", { className: "border rounded p-3" })
          if (asset.thumbnail_url) item.append(c.node("img", { src: asset.thumbnail_url, alt: "", className: "max-w-full h-auto" }))
          item.append(c.button(`Wybierz: ${asset.title}`, () => {
            selectMediaAsset(this.rows, key, asset)
            this.dialog.close(); refresh(); c.changed()
            // refresh replaced the opener; restore focus to its replacement.
            const host = key === "hero" ? c.heroTarget : c.card(key)
            this.returnFocus = [...host.querySelectorAll("button")].find(button => button.textContent === "Zmień obraz")
            this.returnFocus?.focus()
          }))
          results.append(item)
        })
        if (page > 1) results.append(c.button("Poprzednia strona obrazów", () => { page--; load() }))
        if (data.more) results.append(c.button("Następna strona obrazów", () => { page++; load() }))
      } catch (error) {
        if (error.name !== "AbortError") results.replaceChildren(c.node("p", { textContent: "Nie udało się pobrać obrazów. Użyj Szukaj ponownie; zmiany formularza są zachowane." }))
      }
    }
    search.addEventListener("keydown", event => {
      if (event.key === "Enter") { event.preventDefault(); page = 1; load() }
    })
    this.dialog.append(c.button("Szukaj", () => { page = 1; load() }), c.button("Zamknij wybór obrazu", () => this.dialog.close()), results)
    this.dialog.showModal(); search.focus(); load()
  }
}
